import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { inboxApi } from "./api";
import { useCrmContact, type CrmContact } from "./crm";
import type {
  ConversationResponse,
  ConversationsResponse,
  CreateManualNoteRequest,
  EligibleApplication,
  MessageChannel,
  SendChatRequest,
  SendEmailRequest,
  SendSmsRequest
} from "../types";

// Per ticket: only these are ineligible for chat; every other status is fine.
const INELIGIBLE_CHAT_STATUSES = ["pending", "canceled", "merged"];

// Eligible applications derived from the CRM contact's applicants (no dedicated endpoint).
const deriveEligibleApplications = (
  contact: CrmContact | undefined
): EligibleApplication[] => {
  const seen = new Set<number>();
  const eligible: EligibleApplication[] = [];
  for (const applicant of contact?.applicants ?? []) {
    const app = applicant.application;
    if (!app || INELIGIBLE_CHAT_STATUSES.includes(app.status)) continue;
    if (seen.has(app.id)) continue;
    seen.add(app.id);
    eligible.push({
      application_id: app.id,
      address_label: app.property?.name ?? "",
      status: app.status,
      application_updated_at: app.updated_at ?? app.created_at ?? ""
    });
  }
  return eligible;
};

interface PaginatedConversations {
  pageParams: unknown[];
  pages: ConversationsResponse[];
}

const updateConversationUnread = (
  oldData: PaginatedConversations | undefined,
  conversationId: string
): PaginatedConversations | undefined => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      conversations: page.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      )
    }))
  };
};

export const inboxKeys = {
  all: ["INBOX"],
  conversations: () => [...inboxKeys.all, "CONVERSATIONS"],
  conversationsList: (filters?: {
    page?: number;
    per_page?: number;
    unreads_only?: boolean;
  }) => [...inboxKeys.conversations(), "LIST", filters],
  conversation: (id: string) => [...inboxKeys.conversations(), id],
  sendOptions: (conversationId: string) => [
    ...inboxKeys.conversation(conversationId),
    "SEND_OPTIONS"
  ],
  smsOptOuts: (conversationId: string) => [
    ...inboxKeys.conversation(conversationId),
    "SMS_OPT_OUTS"
  ],
  messages: (
    conversationId: string,
    params?: { limit?: number; channel?: MessageChannel; anchor?: string }
  ) => [...inboxKeys.conversation(conversationId), "MESSAGES", params],
  contacts: () => [...inboxKeys.all, "CONTACTS"],
  contactsSearch: (query: string) => [...inboxKeys.contacts(), "SEARCH", query],
  contactRelated: (contactId: string) => [
    ...inboxKeys.all,
    "CONTACTS",
    contactId,
    "RELATED"
  ],
  search: (q: string) => [...inboxKeys.all, "SEARCH", q],
  searchHistory: () => [...inboxKeys.all, "SEARCH", "HISTORY"],
  eligibleApplications: (contactId: string) => [
    ...inboxKeys.all,
    "CONTACTS",
    contactId,
    "ELIGIBLE_APPLICATIONS"
  ]
};

export const useConversations = (params?: {
  per_page?: number;
  unreads_only?: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: inboxKeys.conversationsList(params),
    queryFn: ({ pageParam = 1 }) =>
      inboxApi.listConversations({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { total } = lastPage.pagination;
      const fetched = allPages.flatMap((p) => p.conversations).length;
      return fetched < total ? allPages.length + 1 : undefined;
    }
  });
};

export const useConversation = (id: string) => {
  return useQuery({
    queryKey: inboxKeys.conversation(id),
    queryFn: () => inboxApi.getConversation(id),
    enabled: !!id
  });
};

// Addresses the current user may send to for this conversation's contact.
// Drives the To picker so a restricted user can't pick an address they lack
// access to (backend also enforces with a 403).
export const useSendOptions = (conversationId?: string) => {
  return useQuery({
    queryKey: inboxKeys.sendOptions(conversationId ?? ""),
    queryFn: () => inboxApi.getSendOptions(conversationId as string),
    enabled: !!conversationId
  });
};

export const useEligibleApplications = (contactId: string) => {
  const { data: contact, ...rest } = useCrmContact(contactId);
  const data = useMemo(
    () => (contact ? deriveEligibleApplications(contact) : undefined),
    [contact]
  );
  return { ...rest, data };
};

export const useMessages = (
  conversationId: string,
  params?: {
    limit?: number;
    channel?: "email" | "sms" | "note" | "chat";
    anchor?: string;
  }
) => {
  const isAnchorMode = !!params?.anchor;

  return useInfiniteQuery({
    queryKey: inboxKeys.messages(conversationId, params),
    queryFn: ({ pageParam, direction }) => {
      // Initial fetch in anchor mode: load messages around the target

      if (pageParam === undefined && isAnchorMode)
        return inboxApi.getMessages(conversationId, {
          limit: params.limit,
          channel: params.channel,
          anchor: params.anchor
        });

      // Initial fetch in normal mode: load newest messages (no cursor)
      if (pageParam === undefined)
        return inboxApi.getMessages(conversationId, {
          limit: params?.limit,
          channel: params?.channel
        });

      // Subsequent fetches: use direction from React Query
      const fetchDirection = direction === "backward" ? "before" : "after";

      return inboxApi.getMessages(conversationId, {
        limit: params?.limit,
        channel: params?.channel,
        cursor: pageParam,
        direction: fetchDirection
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (isAnchorMode)
        return lastPage.has_more_after
          ? (lastPage.after_cursor ?? undefined)
          : undefined;

      // Normal mode: no forward loading (we start at newest)
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (isAnchorMode)
        return firstPage.has_more_before
          ? (firstPage.before_cursor ?? undefined)
          : undefined;

      // Non-anchor mode: use before_cursor (oldest message) for older messages
      return firstPage.has_more
        ? (firstPage.before_cursor ?? undefined)
        : undefined;
    },
    enabled: !!conversationId
  });
};

// Numbers the conversation contact has currently opted out of SMS. Used by the
// SMS composer to disable/block texting those numbers. Read-only query — mirrors
// the no-toast convention of the sibling read hooks above; the composer fails
// open (send stays enabled) if this cannot load.
export const useSmsOptOuts = (conversationId: string) => {
  return useQuery({
    queryKey: inboxKeys.smsOptOuts(conversationId),
    queryFn: () => inboxApi.getSmsOptOuts(conversationId),
    enabled: !!conversationId
  });
};

export const useInboxSearch = (q: string) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: inboxKeys.search(q),
    queryFn: () => inboxApi.search(q),
    enabled: q.trim().length >= 2
  });

  useEffect(() => {
    if (query.data)
      queryClient.invalidateQueries({
        queryKey: inboxKeys.searchHistory()
      });
  }, [query.data, queryClient]);

  return query;
};

export const useSearchHistory = () => {
  return useQuery({
    queryKey: inboxKeys.searchHistory(),
    queryFn: () => inboxApi.getSearchHistory()
  });
};

export const useCommitSearch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (q: string) => inboxApi.search(q),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.searchHistory()
      });
    }
  });
};

interface MessagePage {
  messages: unknown[];
  [key: string]: unknown;
}

interface PaginatedMessages {
  pageParams: unknown[];
  pages: MessagePage[];
}

const optimisticAppendMessage = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  response: { message?: unknown } | unknown
) => {
  const newMessage = (response as { message?: unknown }).message || response;

  queryClient.setQueriesData(
    {
      queryKey: [...inboxKeys.conversation(conversationId), "MESSAGES"]
    },
    (oldData: PaginatedMessages | undefined) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pageParams: oldData.pageParams,
        pages: oldData.pages.map((page: MessagePage, index: number) => {
          if (index === oldData.pages.length - 1)
            return {
              ...page,
              messages: [...page.messages, newMessage]
            };

          return page;
        })
      };
    }
  );

  queryClient.invalidateQueries({
    queryKey: inboxKeys.conversation(conversationId),
    exact: true,
    refetchType: "none"
  });

  queryClient.invalidateQueries({
    queryKey: [...inboxKeys.conversations(), "LIST"],
    refetchType: "none"
  });
};

export const useCreateManualNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateManualNoteRequest) =>
      inboxApi.createManualNote(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => inboxApi.markAsRead(conversationId),
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"]
      });

      // Optimistic update: set unread_count to 0 in conversation list cache
      queryClient.setQueriesData(
        { queryKey: [...inboxKeys.conversations(), "LIST"] },
        (oldData: PaginatedConversations | undefined) =>
          updateConversationUnread(oldData, conversationId)
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"],
        refetchType: "active"
      });
    }
  });
};

export const useRetryEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => inboxApi.retryEmail(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.all,
        refetchType: "active"
      });
    }
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => inboxApi.deleteMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.all,
        refetchType: "active"
      });
    }
  });
};

export const useSetAiSuggestions = (conversationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      ...inboxKeys.conversation(conversationId),
      "AI_SUGGESTIONS",
      "UPDATE"
    ],
    mutationFn: (enabled: boolean) =>
      inboxApi.setAiSuggestions(conversationId, enabled),
    onMutate: async (enabled) => {
      const key = inboxKeys.conversation(conversationId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ConversationResponse>(key);
      queryClient.setQueryData<ConversationResponse>(key, (old) =>
        old?.conversation
          ? {
              ...old,
              conversation: {
                ...old.conversation,
                ai_suggestions_enabled: enabled
              }
            }
          : old
      );
      return { previous };
    },
    onError: (_err, _enabled, context) => {
      if (context?.previous)
        queryClient.setQueryData(
          inboxKeys.conversation(conversationId),
          context.previous
        );
      toast.error("Failed to update AI suggestions setting");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.conversation(conversationId),
        exact: true
      });
    }
  });
};

export const useDismissSuggestion = () => {
  return useMutation({
    mutationKey: [...inboxKeys.all, "SUGGESTIONS", "DISMISS"],
    mutationFn: (anchorMessageId: string) =>
      inboxApi.dismissSuggestion(anchorMessageId),
    onError: () => {
      toast.error("Failed to dismiss suggestion");
    }
  });
};

export const useRetrySuggestion = () => {
  return useMutation({
    mutationKey: [...inboxKeys.all, "SUGGESTIONS", "RETRY"],
    mutationFn: (anchorMessageId: string) =>
      inboxApi.retrySuggestion(anchorMessageId),
    onSuccess: (data) => {
      // The backend no-ops with ok:false when there is no failed suggestion
      // for this anchor anymore (e.g. resolved in another tab) — no WS event
      // will follow, so tell the user instead of leaving a dead Retry button.
      if (!data.ok) toast.error("This suggestion is no longer available");
    },
    onError: () => {
      toast.error("Failed to retry suggestion");
    }
  });
};

// Edit-capture telemetry for AI-216 — best-effort by design: never block or
// alarm the user over a lost signal, but keep the failure observable.
export const useResolveSuggestion = () => {
  return useMutation({
    mutationKey: [...inboxKeys.all, "SUGGESTIONS", "RESOLVE"],
    mutationFn: (payload: {
      anchor_message_id: string;
      final_sent_text: string;
      outbound_message_id?: string;
    }) => inboxApi.resolveSuggestion(payload),
    onError: (err) => {
      console.warn("[inbox] Failed to record suggestion resolution:", err);
    }
  });
};

export const useSendEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendEmailRequest) => inboxApi.sendEmail(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

export const useSendSms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendSmsRequest) => inboxApi.sendSms(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

export const useSendChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendChatRequest) => inboxApi.sendChat(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB (server limit)

// Upload a chat attachment: request a presigned URL, PUT the file to S3, confirm.
// Returns the attachment id to pass as attachment_ids on send.
export const useUploadChatAttachment = () =>
  useMutation({
    mutationFn: async (
      file: File
    ): Promise<{ id: string; filename: string }> => {
      if (file.size > MAX_ATTACHMENT_BYTES) throw new Error("file-too-large");
      const { id, upload_url: uploadUrl } = await inboxApi.requestChatUpload({
        filename: file.name,
        content_type: file.type,
        size: file.size
      });
      await inboxApi.uploadToPresignedUrl(uploadUrl, file);
      await inboxApi.confirmChatUpload(id);
      return { id, filename: file.name };
    }
  });
